import React from 'react';
import {
    Building2, ChevronRight, Crosshair, ExternalLink, Info, Layers, MapPin, PieChart, Target, TrendingUp, X,
} from 'lucide-react';
import { BRANCHE_UNGEPFLEGT, brancheLabel } from '../../../services/branchen';
import { leadStatusLabel } from '../../../services/leadLogic';
import { MapFeature } from '../../../services/mapFeatures';
import {
    BranchenAnteil,
    CrmMapMetrik,
    RegionAggregate,
    UNBEKANNT_KEY,
    WhiteSpot,
    computeConversion,
    fristTage,
    istGeringeDatenbasis,
    metrikFallzahl,
    metrikWert,
} from '../../../services/crmMapData';
import { formatMetrik } from '../../../hooks/useCrmMap';

// Schwebende Panels der CRM-Karte. Rein praesentational: alle Zahlen kommen
// fertig gerechnet aus crmMapData.ts bzw. useCrmMap.
//
// Diese Panels sind zugleich die LISTEN-ALTERNATIVE zur Karte (Plan Abschnitt
// 11, „Accessibility"): jede Information, die eine Flaeche oder ein Marker
// traegt, steht hier auch als Text — Regionsliste mit Werten, Weiße-Flecken-
// Ranking, Branchenverteilung, Detail zum ausgewaehlten Objekt. Wer die Karte
// nicht bedienen kann oder will, verliert nichts.

const eur0 = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

/** Farben des Donuts. Bewusst kategorial (keine Rangfolge) und deutlich trennbar. */
const DONUT_FARBEN = [
    '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444',
    '#06b6d4', '#ec4899', '#84cc16', '#6366f1', '#f97316',
];
const DONUT_FARBE_OHNE = '#cbd5e1';

const donutFarbe = (index: number, branche: string): string =>
    branche === '' ? DONUT_FARBE_OHNE : DONUT_FARBEN[index % DONUT_FARBEN.length];

// ─── Rahmen ─────────────────────────────────────────────────────────────────

const Panel: React.FC<{
    title: string;
    icon: React.ReactNode;
    hint?: string;
    children: React.ReactNode;
}> = ({ title, icon, hint, children }) => (
    <section className="glass rounded-2xl overflow-hidden">
        <header className="px-4 py-2.5 border-b border-white/60 flex items-center justify-between gap-2">
            <h3 className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                {icon} {title}
            </h3>
            {hint && <span className="text-[10px] text-slate-400 text-right leading-tight">{hint}</span>}
        </header>
        <div className="p-4">{children}</div>
    </section>
);

const Leer: React.FC<{ text: string }> = ({ text }) => (
    <p className="py-6 text-center text-[11px] text-slate-400">{text}</p>
);

/** Markierung fuer Aussagen auf duenner Datenbasis (Plan Abschnitt 6). */
export const DatenbasisHinweis: React.FC<{ fallzahl: number; was?: string }> = ({ fallzahl, was = 'Fälle' }) => {
    if (!istGeringeDatenbasis(fallzahl)) return null;
    return (
        <span
            className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-600"
            title={`Geringe Datenbasis: nur ${fallzahl} ${was}. Die Aussage ist mit Vorsicht zu lesen.`}
        >
            <Info className="w-2.5 h-2.5" /> dünn
        </span>
    );
};

// ─── Branchen-Donut ─────────────────────────────────────────────────────────

interface DonutSegment {
    b: BranchenAnteil;
    /** Bogenlaenge in Pixeln entlang des Kreisumfangs. */
    laenge: number;
    /** Startversatz, ebenfalls in Umfangs-Pixeln. */
    start: number;
}

interface DonutProps {
    branchen: BranchenAnteil[];
    /** null = kein Filter aktiv. '' = „ohne Branche". */
    aktiv: string | null;
    onSelect: (branche: string | null) => void;
}

/**
 * Donut aus einem einzigen SVG-Kreis mit `stroke-dasharray` — kein
 * Chart-Paket. Das Projekt zieht bewusst keine Chart-Library (siehe
 * QuoteCharts.tsx); fuer fuenf bis zehn Segmente ist eine eigene Berechnung
 * kuerzer als die Anbindung einer.
 *
 * Klickbar als Filter (Plan Abschnitt 6). Ein zweiter Klick auf dasselbe
 * Segment hebt den Filter wieder auf — sonst muesste man raten, wie man ihn
 * loswird.
 */
export const BranchenDonut: React.FC<DonutProps> = ({ branchen, aktiv, onSelect }) => {
    const gesamt = branchen.reduce((s, b) => s + b.gesamt, 0);
    if (gesamt === 0) return <Leer text="Keine Kunden oder Leads mit Koordinaten." />;

    const R = 42;
    const UMFANG = 2 * Math.PI * R;

    // Bogenlaengen und Startversatz VOR dem Rendern ausrechnen. Ein waehrend
    // des Renderns hochgezaehlter Zaehler waere bei einem abgebrochenen Render
    // falsch aufgelaufen (React darf einen Renderlauf verwerfen und wiederholen).
    const segmente: DonutSegment[] = [];
    for (const b of branchen) {
        const start = segmente.length
            ? segmente[segmente.length - 1].start + segmente[segmente.length - 1].laenge
            : 0;
        segmente.push({ b, laenge: (b.gesamt / gesamt) * UMFANG, start });
    }

    return (
        <div className="flex items-center gap-4">
            <svg viewBox="0 0 120 120" className="w-24 h-24 shrink-0 -rotate-90" role="img" aria-label="Branchenverteilung">
                {segmente.map(({ b, laenge, start }, i) => {
                    const dash = `${laenge} ${UMFANG - laenge}`;
                    const dashOffset = -start;
                    const gedimmt = aktiv !== null && aktiv !== b.branche;
                    return (
                        <circle
                            key={b.branche || '__ohne__'}
                            cx="60"
                            cy="60"
                            r={R}
                            fill="none"
                            stroke={donutFarbe(i, b.branche)}
                            strokeWidth={aktiv === b.branche ? 20 : 16}
                            strokeDasharray={dash}
                            strokeDashoffset={dashOffset}
                            opacity={gedimmt ? 0.25 : 1}
                            className="cursor-pointer transition-all"
                            onClick={() => onSelect(aktiv === b.branche ? null : b.branche)}
                        >
                            <title>{`${b.branche ? brancheLabel(b.branche) : BRANCHE_UNGEPFLEGT}: ${b.gesamt}`}</title>
                        </circle>
                    );
                })}
                <text
                    x="60"
                    y="60"
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="rotate-90 origin-center fill-slate-700 font-bold"
                    style={{ fontSize: 18 }}
                >
                    {gesamt}
                </text>
            </svg>

            <ul className="flex-1 min-w-0 space-y-0.5 max-h-32 overflow-y-auto no-scrollbar">
                {branchen.map((b, i) => (
                    <li key={b.branche || '__ohne__'}>
                        <button
                            type="button"
                            onClick={() => onSelect(aktiv === b.branche ? null : b.branche)}
                            aria-pressed={aktiv === b.branche}
                            className={`w-full flex items-center gap-1.5 text-left text-[11px] px-1 py-0.5 rounded transition-colors ${
                                aktiv === b.branche ? 'bg-blue-100/70 font-bold text-blue-700' : 'hover:bg-white/60 text-slate-600'
                            }`}
                        >
                            <span
                                className="w-2 h-2 rounded-sm shrink-0"
                                style={{ backgroundColor: donutFarbe(i, b.branche) }}
                            />
                            <span className="truncate">{b.branche ? brancheLabel(b.branche) : BRANCHE_UNGEPFLEGT}</span>
                            <span className="ml-auto tabular-nums text-slate-400">{b.gesamt}</span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
};

// ─── Conversion ─────────────────────────────────────────────────────────────

/**
 * Abschlussquote der aktuellen Auswahl. Der Hinweistext nennt ausdruecklich,
 * WAS im Nenner steht — ohne ihn liest jeder die Zahl anders (Plan Abschnitt 6:
 * Bestandskunden ohne Lead-Historie zaehlen nicht mit).
 */
export const ConversionKachel: React.FC<{ regionen: RegionAggregate[]; titel: string }> = ({ regionen, titel }) => {
    const summe = regionen.reduce(
        (acc, r) => ({
            leadsGewonnen: acc.leadsGewonnen + r.leadsGewonnen,
            leadsVerloren: acc.leadsVerloren + r.leadsVerloren,
        }),
        { leadsGewonnen: 0, leadsVerloren: 0 },
    );
    const c = computeConversion(summe);

    return (
        <div>
            <div className="flex items-baseline gap-2">
                <span className="text-2xl font-black text-slate-800 tabular-nums">
                    {c.prozent === null ? '—' : `${c.prozent} %`}
                </span>
                <DatenbasisHinweis fallzahl={c.basis} was="entschiedene Leads" />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
                {titel}: {c.gewonnen} gewonnen, {c.verloren} verloren
                {c.basis === 0 && ' — noch keine entschiedene Lead-Kohorte'}
            </p>
            <p className="text-[10px] text-slate-400 mt-1 leading-snug">
                gewonnen / (gewonnen + verloren). Offene Leads und Bestandskunden ohne Lead-Historie zählen nicht.
            </p>
        </div>
    );
};

// ─── Weiße Flecken ──────────────────────────────────────────────────────────

interface WhiteSpotProps {
    spots: WhiteSpot[];
    /** Name zum AGS, sofern die Kreisgeometrie geladen ist. */
    nameFuer: (ags: string) => string | null;
    onZoom: (spot: WhiteSpot) => void;
}

export const WeisseFleckenListe: React.FC<WhiteSpotProps> = ({ spots, nameFuer, onZoom }) => {
    if (spots.length === 0) {
        return <Leer text="Keine weißen Flecken — überall, wo GEFAS präsent ist, gibt es auch Kunden." />;
    }
    return (
        <ol className="space-y-1.5 max-h-56 overflow-y-auto no-scrollbar">
            {spots.slice(0, 25).map((s, i) => (
                <li key={s.key}>
                    <button
                        type="button"
                        onClick={() => onZoom(s)}
                        className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-white/70 transition-colors group"
                    >
                        <span className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-black flex items-center justify-center shrink-0 tabular-nums">
                                {i + 1}
                            </span>
                            <span className="text-[11px] font-bold text-slate-700 truncate">
                                {/* Der Kreisname steht in landkreise.geo.json und ist erst nach
                                    dem ersten Drilldown geladen (739 KB, bewusst lazy). Bis
                                    dahin ist der amtliche Schluessel die ehrliche Beschriftung. */}
                                {nameFuer(s.key) ?? `Kreis ${s.key}`}
                                {s.bundeslandKuerzel && (
                                    <span className="text-slate-400 font-normal"> ({s.bundeslandKuerzel})</span>
                                )}
                            </span>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-300 ml-auto shrink-0 group-hover:text-blue-500" />
                        </span>
                        <span className="block text-[11px] text-slate-500 mt-0.5 pl-7">{s.begruendung}</span>
                    </button>
                </li>
            ))}
        </ol>
    );
};

// ─── Regionsliste (Listen-Alternative zur Flaeche) ──────────────────────────

interface RegionListeProps {
    regionen: RegionAggregate[];
    metrik: CrmMapMetrik;
    nameFuer: (ags: string) => string | null;
    onSelect: (region: RegionAggregate) => void;
    aktiverKey?: string;
}

export const RegionListe: React.FC<RegionListeProps> = ({ regionen, metrik, nameFuer, onSelect, aktiverKey }) => {
    if (regionen.length === 0) return <Leer text="Keine Regionsdaten — Aggregate fehlen oder Zeitraum ist leer." />;
    return (
        <ul className="space-y-0.5 max-h-64 overflow-y-auto no-scrollbar" aria-label="Regionen">
            {regionen.map((r) => {
                const wert = metrikWert(r, metrik);
                const fallzahl = metrikFallzahl(r, metrik);
                return (
                    <li key={r.key}>
                        <button
                            type="button"
                            onClick={() => onSelect(r)}
                            aria-current={aktiverKey === r.key ? 'true' : undefined}
                            className={`w-full flex items-center gap-2 text-left px-2 py-1 rounded-lg transition-colors ${
                                aktiverKey === r.key ? 'bg-blue-100/70' : 'hover:bg-white/70'
                            }`}
                        >
                            <span className="text-[11px] text-slate-700 truncate flex-1 min-w-0">
                                {r.unbekannt ? (
                                    <span className="italic text-slate-500">Nicht zuordenbar</span>
                                ) : (
                                    nameFuer(r.key) ?? r.key
                                )}
                            </span>
                            <DatenbasisHinweis fallzahl={fallzahl} />
                            <span className="text-[11px] font-bold text-slate-700 tabular-nums shrink-0">
                                {wert === null ? '—' : formatMetrik(wert, metrik)}
                            </span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};

// ─── KPI-Panel (links) ──────────────────────────────────────────────────────

interface KpiPanelProps {
    regionen: RegionAggregate[];
    metrik: CrmMapMetrik;
    branchen: BranchenAnteil[];
    brancheFilter: string | null;
    onBrancheFilter: (b: string | null) => void;
    weisseFlecken: WhiteSpot[];
    nameFuer: (ags: string) => string | null;
    onZoomSpot: (spot: WhiteSpot) => void;
    onSelectRegion: (region: RegionAggregate) => void;
    aktiverRegionKey?: string;
    ebenenTitel: string;
}

export const CrmMapKpiPanel: React.FC<KpiPanelProps> = ({
    regionen, metrik, branchen, brancheFilter, onBrancheFilter,
    weisseFlecken, nameFuer, onZoomSpot, onSelectRegion, aktiverRegionKey, ebenenTitel,
}) => {
    const umsatz = regionen.reduce((s, r) => s + r.nettoSumme, 0);
    const kunden = regionen.reduce((s, r) => s + r.kunden, 0);
    const leadsOffen = regionen.reduce((s, r) => s + r.leadsOffen, 0);
    const unbekannt = regionen.find((r) => r.key === UNBEKANNT_KEY);

    return (
        <div className="space-y-3">
            <Panel title="Überblick" icon={<TrendingUp className="w-3.5 h-3.5 text-blue-500" />} hint={ebenenTitel}>
                <dl className="grid grid-cols-3 gap-2 text-center">
                    <div>
                        <dt className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Umsatz</dt>
                        <dd className="text-sm font-black text-slate-800 tabular-nums">{eur0.format(umsatz / 100)}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Kunden</dt>
                        <dd className="text-sm font-black text-slate-800 tabular-nums">{kunden}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Offene Leads</dt>
                        <dd className="text-sm font-black text-slate-800 tabular-nums">{leadsOffen}</dd>
                    </div>
                </dl>
                {unbekannt && (
                    // Eigene Kachel statt stillem Weglassen (Plan Abschnitt 6).
                    <p className="mt-2 pt-2 border-t border-white/60 text-[11px] text-slate-500">
                        Nicht zuordenbar: {eur0.format(unbekannt.nettoSumme / 100)} · {unbekannt.kunden} Kunden ·{' '}
                        {unbekannt.leadsOffen} offene Leads
                        <span className="block text-[10px] text-slate-400">
                            PLZ fehlt oder ist in der Zuordnungstabelle unbekannt.
                        </span>
                    </p>
                )}
            </Panel>

            <Panel
                title="Conversion"
                icon={<Target className="w-3.5 h-3.5 text-emerald-500" />}
                hint="nur Lead-Kohorte"
            >
                <ConversionKachel regionen={regionen} titel={ebenenTitel} />
            </Panel>

            <Panel
                title="Branchen"
                icon={<PieChart className="w-3.5 h-3.5 text-indigo-500" />}
                hint={brancheFilter !== null ? 'Filter aktiv — erneut klicken hebt ihn auf' : 'klickbar als Filter'}
            >
                <BranchenDonut branchen={branchen} aktiv={brancheFilter} onSelect={onBrancheFilter} />
            </Panel>

            <Panel
                title="Weiße Flecken"
                icon={<Crosshair className="w-3.5 h-3.5 text-rose-500" />}
                hint="Kreise mit Präsenz, ohne Kunden"
            >
                <WeisseFleckenListe spots={weisseFlecken} nameFuer={nameFuer} onZoom={onZoomSpot} />
            </Panel>

            <Panel
                title="Regionen"
                icon={<Layers className="w-3.5 h-3.5 text-slate-500" />}
                hint="Listenansicht der Karte"
            >
                <RegionListe
                    regionen={regionen}
                    metrik={metrik}
                    nameFuer={nameFuer}
                    onSelect={onSelectRegion}
                    aktiverKey={aktiverRegionKey}
                />
            </Panel>
        </div>
    );
};

// ─── Detail-Panel (rechts) ──────────────────────────────────────────────────

const KIND_LABELS: Record<string, string> = {
    customer: 'Kunde',
    lead: 'Lead',
    project: 'Projekt',
    tender: 'Ausschreibung',
    freelancer: 'Freelancer',
};

interface DetailProps {
    feature: MapFeature | null;
    region: RegionAggregate | null;
    regionName?: string | null;
    metrik: CrmMapMetrik;
    /** Liste hinter einem angeklickten Cluster (a11y: Cluster ohne Zoom lesbar). */
    clusterFeatures?: MapFeature[];
    onSelectFeature: (f: MapFeature) => void;
    onClose: () => void;
    onOpenCustomer?: (customerId: string) => void;
    onOpenProject?: (projectId: string) => void;
    heuteIso?: string;
}

export const CrmMapDetailPanel: React.FC<DetailProps> = ({
    feature, region, regionName, metrik, clusterFeatures, onSelectFeature, onClose,
    onOpenCustomer, onOpenProject, heuteIso,
}) => {
    if (!feature && !region && !clusterFeatures?.length) return null;

    return (
        <section className="glass rounded-2xl overflow-hidden w-72">
            <header className="px-4 py-2.5 border-b border-white/60 flex items-center justify-between gap-2">
                <h3 className="text-xs font-bold text-slate-700 truncate">
                    {feature ? KIND_LABELS[feature.kind] ?? 'Objekt' : clusterFeatures?.length ? 'Gruppe' : 'Region'}
                </h3>
                <button type="button" onClick={onClose} aria-label="Detail schließen" className="text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                </button>
            </header>

            <div className="p-4 max-h-[60vh] overflow-y-auto no-scrollbar">
                {feature && <FeatureDetail feature={feature} onOpenCustomer={onOpenCustomer} onOpenProject={onOpenProject} heuteIso={heuteIso} />}

                {!feature && !!clusterFeatures?.length && (
                    <ul className="space-y-0.5">
                        {clusterFeatures.map((f) => (
                            <li key={`${f.kind}:${f.id}`}>
                                <button
                                    type="button"
                                    onClick={() => onSelectFeature(f)}
                                    className="w-full text-left px-2 py-1 rounded-lg hover:bg-white/70 text-[11px] text-slate-700 flex items-center gap-2"
                                >
                                    <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                                    <span className="truncate">{f.name}</span>
                                    <span className="ml-auto text-[10px] text-slate-400 shrink-0">
                                        {KIND_LABELS[f.kind] ?? f.kind}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {!feature && !clusterFeatures?.length && region && (
                    <RegionDetail region={region} name={regionName} metrik={metrik} />
                )}
            </div>
        </section>
    );
};

const Zeile: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex gap-2 text-[11px] py-0.5">
        <span className="text-slate-400 w-24 shrink-0">{label}</span>
        <span className="text-slate-700 font-medium min-w-0 break-words">{children}</span>
    </div>
);

const FeatureDetail: React.FC<{
    feature: MapFeature;
    onOpenCustomer?: (id: string) => void;
    onOpenProject?: (id: string) => void;
    heuteIso?: string;
}> = ({ feature: f, onOpenCustomer, onOpenProject, heuteIso }) => {
    const meta = f.meta || {};
    const tage = f.kind === 'tender' ? fristTage(String(meta.abgabefrist ?? ''), heuteIso) : null;

    return (
        <>
            <h4 className="text-sm font-bold text-slate-800 leading-snug mb-2">{f.name}</h4>

            {f.kind === 'lead' && <Zeile label="Status">{leadStatusLabel(f.status)}</Zeile>}
            {f.kind === 'project' && f.status && <Zeile label="Status">{String(f.status)}</Zeile>}
            {!!meta.branche && <Zeile label="Branche">{brancheLabel(String(meta.branche))}</Zeile>}
            {!!meta.customerNumber && <Zeile label="Kundennr.">{String(meta.customerNumber)}</Zeile>}
            {!!meta.projectNumber && <Zeile label="Projektnr.">{String(meta.projectNumber)}</Zeile>}
            {!!meta.auftraggeber && <Zeile label="Auftraggeber">{String(meta.auftraggeber)}</Zeile>}
            {(!!meta.zip || !!meta.city) && (
                <Zeile label="Ort">{[meta.zip, meta.city].filter(Boolean).join(' ')}</Zeile>
            )}
            {!!meta.address && <Zeile label="Adresse">{String(meta.address)}</Zeile>}
            {!!meta.leistungsort && <Zeile label="Leistungsort">{String(meta.leistungsort)}</Zeile>}
            {!!meta.coordinator && <Zeile label="Koordinator">{String(meta.coordinator)}</Zeile>}
            {typeof meta.score === 'number' && <Zeile label="Score">{String(meta.score)}</Zeile>}
            {!!meta.quelle && <Zeile label="Quelle">{String(meta.quelle)}</Zeile>}
            {!!meta.firma && <Zeile label="Firma">{String(meta.firma)}</Zeile>}
            {typeof meta.einsatzradiusKm === 'number' && (
                <Zeile label="Einsatzradius">{String(meta.einsatzradiusKm)} km</Zeile>
            )}
            {typeof meta.rating === 'number' && (
                <Zeile label="Bewertung">{meta.rating.toFixed(1).replace('.', ',')} von 5</Zeile>
            )}
            {/* Ehrliches Etikett zur Markerposition: der Punkt ist auf ~1 km
                gerundet (mapFeatures.freelancerToFeature, Plan-Befund 5.3).
                Ohne diesen Hinweis liest man ihn als Anschrift. */}
            {meta.positionGerundet === true && (
                <p className="mt-2 text-[11px] text-slate-400 leading-snug">
                    Position bewusst grob (~1 km Raster) — die Privatadresse wird auf der Karte nie
                    punktgenau angezeigt. Genaue Anschrift in der Freelancer-Akte.
                </p>
            )}

            {tage !== null && (
                <p
                    className={`mt-2 text-[11px] font-bold ${
                        tage < 0 ? 'text-slate-400' : tage <= 7 ? 'text-rose-600' : 'text-slate-600'
                    }`}
                >
                    {tage < 0
                        ? `Frist abgelaufen (vor ${Math.abs(tage)} Tagen)`
                        : tage === 0
                          ? 'Abgabefrist ist HEUTE'
                          : `Noch ${tage} ${tage === 1 ? 'Tag' : 'Tage'} bis zur Abgabe`}
                </p>
            )}

            {!!meta.dokumenteUrl && (
                <a
                    href={String(meta.dokumenteUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:underline"
                >
                    Vergabeunterlagen <ExternalLink className="w-3 h-3" />
                </a>
            )}

            {f.kind === 'customer' && onOpenCustomer && (
                <button
                    type="button"
                    onClick={() => onOpenCustomer(f.id)}
                    className="mt-3 w-full glass-button rounded-xl py-1.5 text-[11px] font-bold text-slate-700 flex items-center justify-center gap-1.5"
                >
                    <Building2 className="w-3.5 h-3.5" /> Kundenakte öffnen
                </button>
            )}
            {f.kind === 'project' && onOpenProject && (
                <button
                    type="button"
                    onClick={() => onOpenProject(f.id)}
                    className="mt-3 w-full glass-button rounded-xl py-1.5 text-[11px] font-bold text-slate-700 flex items-center justify-center gap-1.5"
                >
                    <MapPin className="w-3.5 h-3.5" /> Projekt öffnen
                </button>
            )}
        </>
    );
};

const RegionDetail: React.FC<{ region: RegionAggregate; name?: string | null; metrik: CrmMapMetrik }> = ({
    region: r,
    name,
    metrik,
}) => {
    const c = computeConversion(r);
    const wert = metrikWert(r, metrik);
    return (
        <>
            <h4 className="text-sm font-bold text-slate-800 mb-2">
                {r.unbekannt ? 'Nicht zuordenbar' : (name ?? r.key)}
            </h4>
            <Zeile label="Umsatz">{eur0.format(r.nettoSumme / 100)} ({r.rechnungen} Belege)</Zeile>
            <Zeile label="Kunden">{r.kunden}</Zeile>
            <Zeile label="Leads offen">{r.leadsOffen}</Zeile>
            <Zeile label="Leads entschieden">
                {c.gewonnen} gewonnen / {c.verloren} verloren
                {c.prozent !== null && ` = ${c.prozent} %`}
            </Zeile>
            <Zeile label="Ausschreibungen">{r.ausschreibungen}</Zeile>
            <Zeile label="Projekte">{r.projekte}</Zeile>
            <Zeile label="Gewählte Metrik">{wert === null ? 'kein Wert' : formatMetrik(wert, metrik)}</Zeile>
            {r.zips.length > 0 && (
                <Zeile label="PLZ">
                    <span className="text-[11px] text-slate-500">
                        {r.zips.slice(0, 20).join(', ')}
                        {r.zips.length > 20 && ` … (+${r.zips.length - 20})`}
                    </span>
                </Zeile>
            )}
        </>
    );
};
